/** 与后端 API 对应的完整领域对象。 */

export type Scope = {
  domains?: string[];
  orgs?: string[];
  people?: string[];
  ips?: string[];
  emails?: string[];
};

export type Classification = 'public' | 'internal' | 'confidential' | 'secret';
export type Priority = 'low' | 'normal' | 'high' | 'urgent';

export const CLASSIFICATION_LABEL: Record<Classification, string> = {
  public: '公开',
  internal: '内部',
  confidential: '机密',
  secret: '绝密',
};

export const PRIORITY_LABEL: Record<Priority, string> = {
  low: '低',
  normal: '常规',
  high: '高',
  urgent: '紧急',
};

export type CaseRole = 'owner' | 'editor' | 'viewer';

export const CASE_ROLE_LABEL: Record<CaseRole, string> = {
  owner: '负责人',
  editor: '编辑',
  viewer: '只读',
};

export type CaseMember = {
  userId: string;
  role: CaseRole;
  roleLabel: string;
  name: string;
  email: string;
  avatar: string | null;
  joinedAt: string;
};

export type Engagement = {
  id: string;
  ownerId: string | null;
  myRole: CaseRole | null;
  caseNo: string | null;
  name: string;
  client: string | null;
  purpose: string | null;
  legalBasis: string | null;
  scope: Scope;
  classification: Classification;
  priority: Priority;
  lead: string | null;
  authorizedBy: string | null;
  authorizedAt: string | null;
  expiresAt: string | null;
  status: 'active' | 'suspended' | 'expired';
  createdAt: string;
  updatedAt: string;
};

export type TargetStatus = 'active' | 'watch' | 'cleared' | 'archived';

export const TARGET_STATUS_LABEL: Record<TargetStatus, string> = {
  active: '在办',
  watch: '盯控',
  cleared: '排除',
  archived: '归档',
};

export type Target = {
  id: string;
  engagementId: string;
  kind: 'person' | 'organization';
  name: string;
  codename: string | null;
  aliases: string[];
  role: string | null;
  org: string | null;
  location: string | null;
  nationality: string | null;
  dob: string | null;
  photo: string | null;
  affiliations: string[];
  idNumbers: Record<string, string>;
  priority: Priority;
  confidence: number;
  status: TargetStatus;
  lastSeenAt: string | null;
  summary: string | null;
  tags: string[];
  riskScore: number;
  riskLevel: 'unknown' | 'low' | 'medium' | 'high' | 'critical';
  createdAt: string;
  updatedAt: string;
};

export type EntityStatus = 'unverified' | 'verified' | 'disputed' | 'inactive';

export const ENTITY_STATUS_LABEL: Record<EntityStatus, string> = {
  unverified: '待核实',
  verified: '已核实',
  disputed: '存疑',
  inactive: '已失效',
};

export type Geo = { lat: number; lng: number; place?: string };

export type Entity = {
  id: string;
  engagementId: string;
  targetId: string | null;
  type: string;
  label: string | null;
  value: string | null;
  aliases: string[];
  confidence: number;
  status: EntityStatus;
  source: string | null;
  tags: string[];
  geo: Geo | null;
  firstSeen: string | null;
  lastSeen: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type RelationStatus = 'active' | 'historical' | 'suspected';
export type RelationDirection = 'forward' | 'both' | 'none';

export const RELATION_STATUS_LABEL: Record<RelationStatus, string> = {
  active: '现存',
  historical: '历史',
  suspected: '存疑',
};

export type Relation = {
  id: string;
  engagementId: string;
  sourceId: string;
  targetId: string;
  type: string;
  label: string | null;
  direction: RelationDirection;
  weight: number;
  strength: number;
  confidence: number;
  status: RelationStatus;
  since: string | null;
  until: string | null;
  evidence: string[];
  createdAt: string;
};

export type CustodyEntry = { at: string; actor: string; action: string };

/** Admiralty 双轴评级：来源可靠性 A–F × 信息可信度 1–6。 */
export type SourceReliability = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';

export const SOURCE_RELIABILITY_LABEL: Record<SourceReliability, string> = {
  A: 'A · 完全可靠',
  B: 'B · 通常可靠',
  C: 'C · 比较可靠',
  D: 'D · 不常可靠',
  E: 'E · 不可靠',
  F: 'F · 无法判断',
};

export const SOURCE_RELIABILITY_SHORT: Record<SourceReliability, string> = {
  A: '可靠',
  B: '通常可靠',
  C: '较可靠',
  D: '不太可靠',
  E: '不可靠',
  F: '未知来源',
};

export const INFO_CREDIBILITY_LABEL: Record<number, string> = {
  1: '1 · 已由其他来源证实',
  2: '2 · 很可能属实',
  3: '3 · 有可能属实',
  4: '4 · 存疑',
  5: '5 · 不太可能',
  6: '6 · 无法判断',
};

export const INFO_CREDIBILITY_SHORT: Record<number, string> = {
  1: '已证实',
  2: '很可能',
  3: '可能',
  4: '存疑',
  5: '不太可能',
  6: '无法判断',
};

/** 综合可信度：A1–C3 视为可用，D/4 以下需谨慎。 */
export function admiraltyScore(source: SourceReliability, credibility: number): number {
  const sourceRank = 'ABCDEF'.indexOf(source);
  const sourceScore = sourceRank < 0 ? 0.5 : 1 - sourceRank / 5;
  const credibilityScore = 1 - (Math.min(6, Math.max(1, credibility)) - 1) / 5;
  return Math.round(((sourceScore * 0.5 + credibilityScore * 0.5) * 100)) / 100;
}

export function admiraltyLevel(source: SourceReliability, credibility: number): 'high' | 'medium' | 'low' {
  const score = admiraltyScore(source, credibility);
  if (score >= 0.7) return 'high';
  if (score >= 0.4) return 'medium';
  return 'low';
}

export type Evidence = {
  id: string;
  engagementId: string;
  targetId: string | null;
  entityId: string | null;
  kind: string;
  title: string | null;
  url: string | null;
  reliability: string;
  sourceReliability: SourceReliability;
  infoCredibility: number;
  hash: string | null;
  content: string | null;
  custody: CustodyEntry[];
  location: string | null;
  witness: string | null;
  seizedAt: string | null;
  collector: string | null;
  collectedAt: string;
};

export type HypothesisStatus = 'open' | 'supported' | 'refuted';

export const HYPOTHESIS_STATUS_LABEL: Record<HypothesisStatus, string> = {
  open: '待验证',
  supported: '已支持',
  refuted: '已排除',
};

export type Hypothesis = {
  id: string;
  engagementId: string;
  targetId: string | null;
  statement: string;
  category: string;
  confidence: number;
  status: HypothesisStatus;
  rationale: string | null;
  alternatives: string[];
  supporting: string[];
  contradicting: string[];
  createdBy: 'ai' | 'human';
  reviewedAt: string | null;
  createdAt: string;
};

/* ── 情报需求（PIR / EEI）────────────────────────────── */
export type RequirementLevel = 'pir' | 'eei' | 'sir';
export type RequirementStatus = 'open' | 'partial' | 'answered' | 'dropped';
export type RequirementPriority = 'low' | 'medium' | 'high' | 'critical';

export type Requirement = {
  id: string;
  engagementId: string;
  code: string | null;
  level: RequirementLevel;
  levelLabel: string;
  question: string;
  priority: RequirementPriority;
  status: RequirementStatus;
  statusLabel: string;
  targetId: string | null;
  answer: string | null;
  evidence: string[];
  ownerId: string | null;
  dueAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

export type RequirementCoverage = {
  total: number;
  answered: number;
  partial: number;
  open: number;
  overdue: number;
  highOpen: number;
  coverage: number;
};

export type TimelineKind =
  | 'event'
  | 'communication'
  | 'movement'
  | 'transaction'
  | 'observation';

export const TIMELINE_KIND_LABEL: Record<TimelineKind, string> = {
  event: '事件',
  communication: '通信',
  movement: '行踪',
  transaction: '交易',
  observation: '观察',
};

export type TimelineEvent = {
  id: string;
  engagementId: string;
  targetId: string | null;
  kind: TimelineKind;
  title: string;
  description: string | null;
  occurredAt: string;
  endedAt: string | null;
  location: string | null;
  participants: string[];
  source: string | null;
  confidence: number;
  createdAt: string;
};

/* ── ACH 矩阵 ───────────────────────────────────────── */
export type AchEntry = {
  id: string;
  hypothesisId: string;
  evidenceId: string;
  rating: '++' | '+' | 'NA' | '-' | '--';
  note: string | null;
  updatedAt: string;
};

export type AchMatrix = {
  cells: AchEntry[];
  hypotheses: { id: string; statement: string; status: string; confidence: number }[];
  evidence: {
    id: string;
    title: string | null;
    kind: string;
    sourceReliability?: string;
    infoCredibility?: number;
  }[];
};

/* ── 证据链完整性 ───────────────────────────────────── */
export type IntegrityReport = {
  engagementId: string;
  total: number;
  ok: boolean;
  broken: { id: string; title: string | null; reason: string; expected: string; actual: string }[];
  lastHash: string | null;
  verifiedAt: string;
};

export type RiskFactor = {
  key: string;
  label: string;
  score: number;
  weight: number;
  rationale: string;
};

export type RiskResult = {
  score: number;
  level: 'unknown' | 'low' | 'medium' | 'high' | 'critical';
  factors: RiskFactor[];
};

export type AiAnalysis = {
  profileSummary: string;
  attackSurface: {
    title: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    detail: string;
    evidenceRefs: string[];
  }[];
  behaviorHypotheses: {
    statement: string;
    category: string;
    confidence: number;
    rationale: string;
  }[];
  osintNextSteps: {
    action: string;
    rationale: string;
    priority: 'low' | 'medium' | 'high';
    legalityNote: string;
  }[];
  pretextIdeas: {
    scenario: string;
    channel: string;
    objective: string;
    safetyNote: string;
  }[];
  defensiveRecommendations: string[];
  riskScoreHint: number;
  disclaimer: string;
  mode: 'remote' | 'heuristic';
  providerError?: string;
};

export type BoardNode = {
  id: string;
  engagementId: string;
  type: string;
  refKind: string | null;
  refId: string | null;
  payload: Record<string, unknown>;
  z: string;
};

export type BoardStyle = 'corkboard' | 'paper' | 'grid';

export const BOARD_STYLE_LABEL: Record<BoardStyle, string> = {
  corkboard: '软木板',
  paper: '纸面',
  grid: '网格',
};

export type BoardData = {
  nodes: BoardNode[];
  viewport: { x: number; y: number; zoom: number };
  style: BoardStyle;
};

export type CollectBundle = {
  input: { kind: string; value: string };
  summary: string;
  evidence: {
    kind: string;
    title: string;
    url: string | null;
    content: string;
    reliability: string;
    collector: string;
    hash: string;
  }[];
  entities: {
    type: string;
    label: string;
    value: string;
    confidence: number;
    source: string;
    metadata?: Record<string, unknown>;
  }[];
  dnsSecurity?: {
    hasSpf?: boolean;
    hasMx?: boolean;
    spfSoftFail?: boolean;
    verificationTokens?: string[];
  } | null;
  rdap?: {
    registrar?: string | null;
    nameservers?: string[];
    events?: { action: string; date: string }[];
  } | null;
};

export type AuditEntry = {
  id: string;
  engagementId: string | null;
  actor: string | null;
  action: string;
  objectType: string | null;
  objectId: string | null;
  detail: unknown;
  at: string;
};

export type Stats = {
  targets: number;
  entities: number;
  evidence: number;
  relations: number;
  hypotheses: number;
  byType: { type: string; n: number }[];
};

/** AI 规划出的可执行动作（与后端 actions.ts 保持一致）。 */
export type AiAction =
  | { id: string; kind: 'bind_entity'; label: string; reason: string; entityId: string; entityLabel: string; targetId: string; targetName: string }
  | { id: string; kind: 'link_entities'; label: string; reason: string; sourceId: string; sourceLabel: string; targetId: string; targetLabel: string; type: string; strength: number }
  | { id: string; kind: 'add_timeline'; label: string; reason: string; title: string; description: string; occurredAt: string; location?: string | null; timelineKind: TimelineKind; targetId?: string | null }
  | { id: string; kind: 'add_hypothesis'; label: string; reason: string; statement: string; category: string; confidence: number; rationale: string; alternatives: string[]; targetId?: string | null }
  | { id: string; kind: 'pin_to_wall'; label: string; reason: string; refKind: 'target' | 'entity' | 'hypothesis'; refId: string; title: string; text: string; color: 'blue' | 'green' | 'purple' | 'red' | 'yellow' | 'orange' | 'default' }
  | { id: string; kind: 'draw_rope'; label: string; reason: string; sourceRefId: string; targetRefId: string; ropeLabel: string }
  | { id: string; kind: 'create_note'; label: string; reason: string; title: string; text: string; color: 'blue' | 'green' | 'purple' | 'red' | 'yellow' | 'orange' | 'default' };

export type OperatePlan = {
  summary: string;
  actions: AiAction[];
  mode: 'heuristic' | 'remote';
  providerError?: string;
};

export type ShareLink = {
  token: string;
  engagementId: string;
  label: string | null;
  expiresAt: string | null;
  revoked: boolean;
  createdAt: string;
};

export type AuthUser = {
  id: string;
  email: string;
  username: string | null;
  name: string;
  role: 'admin' | 'analyst' | 'viewer';
  avatar: string | null;
};

export type AppUser = AuthUser & {
  createdAt: string;
  lastLoginAt: string | null;
  caseCount: number;
};

export const ROLE_LABEL: Record<AuthUser['role'], string> = {
  admin: '管理员',
  analyst: '分析员',
  viewer: '只读',
};

/** 只读共享快照。 */
export type ShareSnapshot = {
  readOnly: true;
  share: { label: string | null; expiresAt: string | null };
  engagement: {
    id: string;
    caseNo: string | null;
    name: string;
    purpose: string | null;
    classification: Classification;
    priority: Priority;
    lead: string | null;
    expiresAt: string | null;
    status: string;
    scope: Scope;
  };
  targets: Target[];
  entities: Entity[];
  relations: Relation[];
  evidence: Evidence[];
  hypotheses: Hypothesis[];
  timeline: TimelineEvent[];
  board: {
    nodes: { id: string; type: string; refKind: string | null; refId: string | null; payload: Record<string, unknown>; z: string }[];
    viewport: { x: number; y: number; zoom: number };
    style: BoardStyle;
  };
  stats: {
    targets: number;
    entities: number;
    relations: number;
    evidence: number;
    hypotheses: number;
    timeline: number;
  };
};

export type AiProviderInfo = {
  id: string;
  name: string;
  baseUrl: string;
  models: string[];
  defaultModel: string;
  enabled: boolean;
  /** env = 环境变量提供；db = 运行期配置 */
  source: 'env' | 'db';
  hasKey: boolean;
  keyHint: string;
};

export type AiStatus = {
  enabled: boolean;
  mode: string;
  model: string;
  providerCount: number;
  defaultProviderId: string | null;
  providers: AiProviderInfo[];
  notice: string;
};

export type AiModelList = {
  providerId: string;
  providerName: string;
  source: 'remote' | 'config';
  models: string[];
  defaultModel: string;
  error?: string;
};

export type MetaConfig = {
  collectEnabled: boolean;
  collectTimeoutMs: number;
  ai: { enabled: boolean; mode: string; model: string };
  capabilities: {
    collectors: string[];
    canvas: string[];
    import: string[];
  };
};